import React, { useEffect, useRef, useState } from 'react';
import {
  Add, ArrowDownward, ArrowUpward, AssignmentTurnedInOutlined, AttachFile,
  Check, CheckBoxOutlined, CreditCardOutlined, DeleteOutline, EmailOutlined,
  EventOutlined, ExpandMore, HelpOutline, LooksOne, PhoneOutlined,
  RadioButtonChecked, ShortText, Subject, ViewListOutlined,
} from '@material-ui/icons';
import ModernSelect from '../../../components/common/ModernSelect';

const FIELD_TYPES = [
  { value: 'text', label: 'Texto corto', description: 'Respuesta breve en una sola línea.', Icon: ShortText },
  { value: 'textarea', label: 'Texto largo', description: 'Respuesta abierta de varios párrafos.', Icon: Subject },
  { value: 'number', label: 'Número', description: 'Acepta únicamente valores numéricos.', Icon: LooksOne },
  { value: 'email', label: 'Correo electrónico', description: 'Valida el formato del correo ingresado.', Icon: EmailOutlined },
  { value: 'tel', label: 'Teléfono', description: 'Solicita un número telefónico de contacto.', Icon: PhoneOutlined },
  { value: 'rut', label: 'RUT', description: 'Solicita el RUT de la persona que postula.', Icon: CreditCardOutlined },
  { value: 'date', label: 'Fecha', description: 'Muestra un selector visual de fecha.', Icon: EventOutlined },
  { value: 'select', label: 'Selección única', description: 'Permite elegir solamente una alternativa.', Icon: RadioButtonChecked },
  { value: 'multiselect', label: 'Selección múltiple', description: 'Permite elegir más de una alternativa.', Icon: CheckBoxOutlined },
  { value: 'consent', label: 'Consentimiento o términos', description: 'Casilla para aceptar una declaración o los términos.', Icon: AssignmentTurnedInOutlined },
  { value: 'file', label: 'Archivo o adjunto', description: 'Solicita un archivo según los formatos y el tamaño configurados.', Icon: AttachFile },
];
const FIELD_TYPE_ALIASES = {
  string: 'text',
  long_text: 'textarea',
  checkbox: 'consent',
  'checkbox-group': 'multiselect',
};
const fieldTypeOptions = type => {
  const canonical = FIELD_TYPE_ALIASES[type] || type;
  const options = FIELD_TYPES.map(option => option.value === canonical
    ? { ...option, value: type }
    : option);
  return FIELD_TYPES.some(option => option.value === canonical) ? options : [
    { value: type, label: type, description: 'Tipo guardado en la configuración.', Icon: HelpOutline },
    ...options,
  ];
};
const DEFAULT_FILE_UPLOAD_LIMITS = {
  max_size_mb: 10,
  allowed_content_types: ['application/pdf', 'image/png', 'image/jpeg'],
  allowed_extensions: ['.pdf', '.png', '.jpg', '.jpeg'],
};
const fileLimits = value => ({ ...DEFAULT_FILE_UPLOAD_LIMITS, ...(value || {}) });
const isObject = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const textValue = value => value === undefined || value === null ? '' : String(value);
const firstDefined = (...values) => values.find(value => value !== undefined && value !== null);
const fieldsOf = step => firstDefined(step.campos, step.fields, []);
const optionsOf = field => firstDefined(field.opciones, field.options, []);
const stepCode = step => textValue(firstDefined(step.bloque, step.block, step.key, step.id));
const fieldType = field => firstDefined(field.tipo, field.type, 'text');
const isApplication = step => firstDefined(step.scope, 'application') === 'application';
const hasOptions = field => ['select', 'multiselect', 'checkbox-group'].includes(fieldType(field));

// Keep whichever legacy alias the saved schema already uses.
const withValue = (source, primary, alias, value) => {
  const key = source[primary] !== undefined || !alias || source[alias] === undefined ? primary : alias;
  const next = { ...source };
  if (value === undefined) {
    delete next[primary];
    if (alias) delete next[alias];
  } else {
    next[key] = value;
  }
  return next;
};

const conditionError = source => {
  for (const name of ['visibleWhen', 'visible_when', 'conditional']) {
    const condition = source[name];
    if (condition === undefined || condition === null || (name === 'conditional' && typeof condition === 'boolean')) continue;
    if (!isObject(condition)) return `La condición ${name} debe ser un objeto JSON.`;
    if (typeof firstDefined(condition.field, condition.key) !== 'string' || !firstDefined(condition.field, condition.key).trim()) {
      return `La condición ${name} debe indicar la clave de la pregunta en "field".`;
    }
    if (condition.in !== undefined && !Array.isArray(condition.in)) return 'La condición "in" debe ser una lista.';
  }
  return '';
};

const validateField = (field, fileUploadLimits) => {
  if (!isObject(field)) return 'La pregunta debe ser un objeto JSON.';
  if (typeof field.key !== 'string' || !field.key.trim()) return 'Cada pregunta necesita un código.';
  if (typeof field.label !== 'string' || !field.label.trim()) return `Completa el texto de la pregunta "${field.key}".`;
  if (field.key.length > 64) return 'El código de pregunta puede tener hasta 64 caracteres.';
  if (['__proto__', 'prototype', 'constructor', 'chatHistory', 'videoFeedback'].includes(field.key) || /(?:File|Error)$/.test(field.key)) return `El código "${field.key}" está reservado. Usa otro código de pregunta.`;
  if (field.label.length > 1000) return `El texto de "${field.key}" puede tener hasta 1000 caracteres.`;
  if (textValue(field.placeholder).length > 500) return `El texto de ejemplo de "${field.key}" puede tener hasta 500 caracteres.`;
  const help = firstDefined(field.ayuda, field.subtitle, '');
  if (textValue(Array.isArray(help) ? help.join(' ') : help).length > 1000) return `La ayuda de "${field.key}" puede tener hasta 1000 caracteres.`;
  if (field.tipo !== undefined && typeof field.tipo !== 'string') return 'El tipo de pregunta debe ser un texto.';
  if (field.type !== undefined && typeof field.type !== 'string') return 'El tipo de pregunta debe ser un texto.';
  if (![...FIELD_TYPES.map(({ value }) => value), ...Object.keys(FIELD_TYPE_ALIASES)].includes(fieldType(field))) return `El tipo de respuesta de "${field.key}" no está disponible.`;
  if (field.requerido !== undefined && typeof field.requerido !== 'boolean') return 'La propiedad requerido debe ser true o false.';
  if (field.required !== undefined && typeof field.required !== 'boolean') return 'La propiedad required debe ser true o false.';
  for (const property of ['maxLength', 'max_length', 'rows', 'maxSelections', 'maxSizeMb']) {
    if (field[property] !== undefined && (typeof field[property] !== 'number' || !Number.isFinite(field[property]) || field[property] <= 0)) {
      return `El valor ${property} de "${field.key}" debe ser mayor que cero.`;
    }
    if (field[property] !== undefined && property !== 'maxSizeMb' && !Number.isInteger(field[property])) {
      return `El valor ${property} de "${field.key}" debe ser un número entero.`;
    }
  }
  if (fieldType(field) === 'file') {
    const limits = fileLimits(fileUploadLimits);
    if (firstDefined(field.maxSizeMb, 5) > limits.max_size_mb) return `El archivo de "${field.key}" no puede superar ${limits.max_size_mb} MB.`;
    const allowed = [...limits.allowed_content_types, ...limits.allowed_extensions].map(type => type.toLowerCase());
    const accepted = textValue(firstDefined(field.accept, '.pdf,.jpg,.jpeg,.png')).split(',').map(type => type.trim().toLowerCase());
    if (accepted.some(type => !allowed.includes(type))) return `Los formatos de "${field.key}" deben ser: ${limits.allowed_extensions.join(', ')}. No se admiten comodines.`;
  }
  const options = optionsOf(field);
  if (!Array.isArray(options)) return `Las opciones de "${field.key}" deben ser una lista.`;
  if (hasOptions(field) && options.length === 0) return `Agrega opciones a la pregunta "${field.key}".`;
  const optionCodes = new Set();
  for (const option of options) {
    if (typeof option !== 'string' && !isObject(option)) return 'Cada opción debe ser un texto o un objeto con value y label.';
    const code = typeof option === 'string' ? option : option.value;
    const label = typeof option === 'string' ? option : option.label;
    if (typeof code !== 'string' || !code.trim() || typeof label !== 'string' || !label.trim()) return `Completa el valor y el texto de todas las opciones de "${field.key}".`;
    if (optionCodes.has(code)) return `El valor de opción "${code}" está repetido en "${field.key}".`;
    optionCodes.add(code);
    const error = typeof option === 'string' ? '' : conditionError(option);
    if (error) return error;
  }
  return conditionError(field);
};

const validateStep = (step, fileUploadLimits) => {
  if (!isObject(step)) return 'La sección debe ser un objeto JSON.';
  if (!stepCode(step).trim()) return 'Cada sección necesita un código de bloque.';
  if (stepCode(step).length > 64) return 'El código de sección puede tener hasta 64 caracteres.';
  const title = firstDefined(step.title, step.titulo);
  if (typeof title !== 'string' || !title.trim()) return `Completa el título de la sección "${stepCode(step)}".`;
  if (title.length > 255) return 'El título de sección puede tener hasta 255 caracteres.';
  if (textValue(firstDefined(step.description, step.descripcion)).length > 1000) return 'La descripción de sección puede tener hasta 1000 caracteres.';
  if (!isApplication(step)) return 'Esta sección corresponde al Formulario 1 y debe mantener scope: "application".';
  const fields = fieldsOf(step);
  if (!Array.isArray(fields)) return `Las preguntas de la sección "${title}" deben ser una lista.`;
  if (step.activo !== false && !fields.some(field => field && field.activo !== false)) return `Agrega al menos una pregunta activa a la sección "${title}".`;
  for (const field of fields) {
    const error = validateField(field, fileUploadLimits);
    if (error) return error;
  }
  return conditionError(step);
};

/** Validate only the editable application form; other scopes are preserved. */
export const validateFormSchema = (value, fileUploadLimits) => {
  if (value === null || value === undefined) return '';
  if (!isObject(value) || !Array.isArray(value.pasos)) return 'El formulario debe contener una lista de secciones en "pasos".';
  if (typeof value.nombre !== 'string' || !value.nombre.trim()) return 'Completa el nombre del formulario.';
  if (value.nombre.length > 255) return 'El nombre del formulario puede tener hasta 255 caracteres.';
  if (value.pasos.length > 100) return 'El formulario puede tener hasta 100 secciones.';
  if (value.pasos.some(step => !isObject(step))) return 'Todas las secciones del formulario deben ser objetos JSON.';
  const steps = value.pasos.filter(isApplication);
  if (!steps.length) return 'Agrega al menos una sección al Formulario 1.';
  if (!steps.some(step => step.activo !== false)) return 'El Formulario 1 necesita al menos una sección activa.';
  const blockCodes = new Set();
  const questionCodes = new Set();
  for (const step of value.pasos) {
    if (isApplication(step)) {
      const error = validateStep(step, fileUploadLimits);
      if (error) return error;
    }
    const code = stepCode(step);
    if (blockCodes.has(code)) return `El código de sección "${code}" está repetido.`;
    blockCodes.add(code);
    for (const field of fieldsOf(step)) {
      if (questionCodes.has(field.key)) return `El código de pregunta "${field.key}" está repetido en el formulario, incluidas las secciones adicionales.`;
      questionCodes.add(field.key);
    }
  }
  return '';
};

const newCode = (prefix, existing) => {
  let index = 1;
  while (existing.includes(`${prefix}_${index}`)) index += 1;
  return `${prefix}_${index}`;
};

const moveItem = (items, index, direction) => {
  const destination = index + direction;
  if (destination < 0 || destination >= items.length) return items;
  const next = items.slice();
  [next[index], next[destination]] = [next[destination], next[index]];
  return next;
};

function JsonEditor({ value, onChange, disabled, validate, label }) {
  const serialized = JSON.stringify(value, null, 2);
  const [draft, setDraft] = useState(serialized);
  const [error, setError] = useState('');
  const input = useRef(null);
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current && !error) setDraft(serialized);
  }, [serialized, error]);
  useEffect(() => {
    if (input.current) input.current.setCustomValidity(error);
  }, [error]);
  const changeDraft = event => {
    const nextDraft = event.target.value;
    setDraft(nextDraft);
    let parsed;
    let nextError = '';
    try {
      parsed = JSON.parse(nextDraft);
      nextError = validate(parsed);
    } catch (ignored) {
      nextError = 'El JSON no es válido. Revisa las comillas, llaves y comas antes de guardar.';
    }
    event.target.setCustomValidity(nextError);
    setError(nextError);
    if (!nextError) onChange(parsed);
  };
  return (
    <details className="premio-editor__advanced">
      <summary>{label}</summary>
      <p className="premio-editor__hint">Configuración opcional para las reglas especiales de esta pregunta o sección. Los cambios se conservarán al guardar el premio.</p>
      <label className="premio-editor__field">
        <span>{label} (JSON)</span>
        <textarea
          ref={input}
          rows={9}
          value={draft}
          disabled={disabled}
          spellCheck={false}
          aria-invalid={Boolean(error)}
          onChange={changeDraft}
          onFocus={() => { focused.current = true; }}
          onBlur={() => { focused.current = false; }}
          onInvalid={event => { event.currentTarget.closest('details').open = true; }}
        />
      </label>
      {error && <p className="premio-editor__error" role="alert">{error}</p>}
    </details>
  );
}

const Control = ({ label, children }) => (
  <label className="premio-editor__field"><span>{label}</span>{children}</label>
);

function OrderButtons({ label, index, length, onMove, onRemove, disabled }) {
  return (
    <div className="announcement-question__actions">
      <button type="button" disabled={disabled || index === 0} onClick={() => onMove(-1)} aria-label={`Subir ${label}`} title={`Subir ${label}`}><ArrowUpward /></button>
      <button type="button" disabled={disabled || index === length - 1} onClick={() => onMove(1)} aria-label={`Bajar ${label}`} title={`Bajar ${label}`}><ArrowDownward /></button>
      <button type="button" className="announcement-question__delete" disabled={disabled} onClick={onRemove} aria-label={`Eliminar ${label}`} title={`Eliminar ${label}`}><DeleteOutline /></button>
    </div>
  );
}

function QuestionEditor({ field, editorId, onChange, disabled, index, length, onMove, onRemove, fileUploadLimits, onValidationError }) {
  const [expanded, setExpanded] = useState(!textValue(field.label).trim());
  const [error, setError] = useState('');
  const container = useRef(null);
  const heading = useRef(null);
  const optionIdentities = useRef(new Map());
  const nextOptionId = useRef(0);
  const optionIdentity = (option, previous) => {
    if (previous !== undefined && optionIdentities.current.has(previous)) {
      optionIdentities.current.set(option, optionIdentities.current.get(previous));
    }
    if (!optionIdentities.current.has(option)) {
      nextOptionId.current += 1;
      optionIdentities.current.set(option, `${editorId}-option-${nextOptionId.current}`);
    }
    return optionIdentities.current.get(option);
  };
  const type = fieldType(field);
  const options = optionsOf(field);
  const update = (primary, alias, nextValue) => onChange(withValue(field, primary, alias, nextValue));
  const updateOptions = next => update('opciones', 'options', next);
  const optionChange = (optionIndex, property, nextValue) => updateOptions(options.map((option, current) => {
    if (current !== optionIndex) return option;
    const normalized = typeof option === 'string' ? { value: option, label: option } : option;
    const next = { ...normalized, [property]: nextValue };
    optionIdentity(next, option);
    return next;
  }));
  const isConsent = ['consent', 'checkbox'].includes(type);
  const limits = fileLimits(fileUploadLimits);
  const changeType = nextType => {
    let next = withValue(field, 'tipo', 'type', nextType);
    if (nextType === 'file') {
      if (next.accept === undefined) next.accept = limits.allowed_extensions.join(',');
      if (next.maxSizeMb === undefined) next.maxSizeMb = Math.min(5, limits.max_size_mb);
    }
    onChange(next);
  };
  const help = firstDefined(field.subtitle, field.ayuda, '');
  const subtitle = Array.isArray(help) ? help.join('\n') : textValue(help);
  const changeHelp = nextText => {
    const nextHelp = Array.isArray(help) ? nextText.split('\n') : nextText;
    // The public form renders subtitle; keep a saved legacy ayuda alias in sync.
    onChange({ ...field, subtitle: nextHelp, ...(field.ayuda !== undefined ? { ayuda: nextHelp } : {}) });
  };
  const finishQuestion = () => {
    const invalidControl = Array.from(container.current.querySelectorAll('input, textarea, select')).find(control => !control.disabled && !control.validity.valid);
    const nextError = validateField(field, fileUploadLimits);
    setError(nextError);
    if (invalidControl) {
      let details = invalidControl.closest('details');
      while (details && container.current.contains(details)) {
        details.open = true;
        details = details.parentElement.closest('details');
      }
      invalidControl.reportValidity();
      invalidControl.focus();
      return;
    }
    if (nextError) {
      onValidationError?.(nextError);
      return;
    }
    setExpanded(false);
    heading.current.focus();
  };
  return (
    <article
      ref={container}
      className={`announcement-question${expanded ? '' : ' announcement-question--collapsed'}`}
      aria-label={`Pregunta ${index + 1}`}
      onInvalidCapture={() => setExpanded(true)}
    >
      <header className="announcement-question__header">
        <h4 className="announcement-question__heading">
          <button
            ref={heading}
            type="button"
            className="announcement-question__toggle"
            id={`${editorId}-heading`}
            aria-expanded={expanded}
            aria-controls={`${editorId}-details`}
            onClick={() => setExpanded(current => !current)}
          >
            <span className="announcement-question__number" aria-hidden="true">{index + 1}</span>
            <strong className="announcement-question__title">{textValue(field.label).trim() || `Pregunta ${index + 1}`}</strong>
            <ExpandMore className="announcement-question__expand-icon" />
          </button>
        </h4>
        <OrderButtons label="pregunta" index={index} length={length} onMove={onMove} onRemove={onRemove} disabled={disabled} />
      </header>
      <div className="announcement-question__body" id={`${editorId}-details`} role="region" aria-labelledby={`${editorId}-heading`} hidden={!expanded}>
        {error && <p className="announcement-question__error" role="alert">{error}</p>}
        <Control label="Pregunta">
          <textarea rows={2} value={textValue(field.label)} required maxLength={1000} placeholder="Escribe lo que necesitas saber de la persona que postula." disabled={disabled} onChange={event => update('label', null, event.target.value)} />
        </Control>
        <div className="announcement-question__grid announcement-question__grid--two">
          <div className="announcement-question__select-field">
            <span>Tipo de respuesta</span>
            <ModernSelect
              ariaLabel="Tipo de respuesta"
              value={type}
              options={fieldTypeOptions(type)}
              disabled={disabled}
              onChange={event => changeType(event.target.value)}
            />
          </div>
          {!isConsent && type !== 'file' && <Control label="Texto de ejemplo">
            <input type="text" value={textValue(field.placeholder)} maxLength={500} disabled={disabled} onChange={event => update('placeholder', null, event.target.value)} />
          </Control>}
        </div>
        <Control label="Ayuda para responder">
          <textarea value={subtitle} rows={2} maxLength={1000} disabled={disabled} onChange={event => changeHelp(event.target.value)} />
        </Control>
        {isConsent && <Control label="Texto del consentimiento">
          <textarea rows={2} value={textValue(field.consentText)} placeholder="Si se deja vacío, se usa el texto de la pregunta." disabled={disabled} onChange={event => update('consentText', null, event.target.value || undefined)} />
        </Control>}
        {hasOptions(field) && <div className="announcement-question__options">
          <span>Opciones de respuesta</span>
          {options.map((option, optionIndex) => <div className="premio-editor__answer-option" key={optionIdentity(option)}>
            <Control label={`Texto de opción ${optionIndex + 1}`}>
              <input type="text" value={typeof option === 'string' ? option : textValue(option.label)} required maxLength={2000} disabled={disabled} onChange={event => optionChange(optionIndex, 'label', event.target.value)} />
            </Control>
            <OrderButtons label={`opción ${optionIndex + 1}`} index={optionIndex} length={options.length} onMove={direction => updateOptions(moveItem(options, optionIndex, direction))} onRemove={() => updateOptions(options.filter((item, current) => current !== optionIndex))} disabled={disabled} />
            {isObject(option) && (option.visibleWhen || option.visible_when) && <span className="premio-editor__hint">Esta opción se muestra según una respuesta anterior.</span>}
          </div>)}
          <button type="button" className="announcement-question__option-add" disabled={disabled} onClick={() => updateOptions([...options, { value: newCode('opcion', options.map(option => typeof option === 'string' ? option : option.value)), label: '' }])}><Add />Agregar opción</button>
        </div>}
        {type === 'file' && <div className="announcement-question__grid announcement-question__grid--two">
          <Control label="Formatos permitidos">
            <input type="text" value={textValue(field.accept)} placeholder={limits.allowed_extensions.join(',')} disabled={disabled} onChange={event => update('accept', null, event.target.value || undefined)} />
          </Control>
          <Control label="Tamaño máximo del archivo (MB)">
            <input type="number" min={0.01} max={limits.max_size_mb} step="any" value={firstDefined(field.maxSizeMb, '')} disabled={disabled} onChange={event => update('maxSizeMb', null, event.target.value === '' ? undefined : Number(event.target.value))} />
          </Control>
          <p className="premio-editor__hint">Formatos disponibles: {limits.allowed_extensions.join(', ')}. Hasta {limits.max_size_mb} MB por archivo.</p>
        </div>}
        <label className="announcement-question__required">
          <input type="checkbox" checked={firstDefined(field.requerido, field.required, true)} disabled={disabled} onChange={event => update('requerido', 'required', event.target.checked)} />
          <span>Respuesta obligatoria</span>
        </label>
        <details className="premio-editor__advanced">
          <summary>Configuración adicional de la pregunta</summary>
          <div className="announcement-question__grid announcement-question__grid--two">
            <Control label="Código de pregunta">
              <input type="text" value={textValue(field.key)} required maxLength={64} disabled={disabled} onChange={event => update('key', null, event.target.value)} />
            </Control>
            <Control label="Máximo de caracteres">
              <input type="number" min={1} step={1} value={firstDefined(field.maxLength, field.max_length, '')} disabled={disabled} onChange={event => update('maxLength', 'max_length', event.target.value === '' ? undefined : Number(event.target.value))} />
            </Control>
            {['textarea', 'long_text'].includes(type) && <Control label="Filas del área de texto">
              <input type="number" min={1} step={1} value={firstDefined(field.rows, '')} disabled={disabled} onChange={event => update('rows', null, event.target.value === '' ? undefined : Number(event.target.value))} />
            </Control>}
            {['multiselect', 'checkbox-group'].includes(type) && <Control label="Máximo de opciones seleccionables">
              <input type="number" min={1} step={1} value={firstDefined(field.maxSelections, '')} disabled={disabled} onChange={event => update('maxSelections', null, event.target.value === '' ? undefined : Number(event.target.value))} />
            </Control>}
          </div>
          <p className="premio-editor__hint">El código identifica las respuestas guardadas. Consérvalo si el premio ya tiene postulaciones.</p>
          {hasOptions(field) && <div className="announcement-question__grid announcement-question__grid--two">
            {options.map((option, optionIndex) => <Control key={optionIdentity(option)} label={`Valor de opción ${optionIndex + 1}`}>
              <input type="text" value={typeof option === 'string' ? option : textValue(option.value)} required maxLength={500} disabled={disabled} onChange={event => optionChange(optionIndex, 'value', event.target.value)} />
            </Control>)}
            <Control label="Mensaje cuando no hay opciones disponibles">
              <input type="text" value={textValue(field.emptyOptionsText)} maxLength={2000} disabled={disabled} onChange={event => update('emptyOptionsText', null, event.target.value)} />
            </Control>
          </div>}
          <JsonEditor label="Opciones avanzadas de la pregunta" value={field} onChange={onChange} disabled={disabled} validate={next => validateField(next, fileUploadLimits)} />
        </details>
        <div className="announcement-question__footer">
          <span>Los cambios se guardarán al finalizar el premio.</span>
          <button type="button" className="announcement-question__save-button" disabled={disabled} onClick={finishQuestion}><Check />Listo</button>
        </div>
      </div>
    </article>
  );
}

export default function FormSchemaEditor({ value, onChange, disabled = false, fileUploadLimits, onValidationError }) {
  // These identities belong to the editor only; never write UI metadata into the schema.
  const identities = useRef(new WeakMap());
  const sequence = useRef(0);
  const identity = (entity, previous, fallback) => {
    if (!isObject(entity)) return `legacy-${fallback || textValue(entity)}`;
    if (isObject(previous) && identities.current.has(previous)) identities.current.set(entity, identities.current.get(previous));
    if (!identities.current.has(entity)) {
      sequence.current += 1;
      identities.current.set(entity, `editor-${sequence.current}`);
    }
    return identities.current.get(entity);
  };
  const makeQuestion = schema => ({
    key: newCode('pregunta', (schema.pasos || []).reduce((result, step) => result.concat(fieldsOf(step).map(field => field.key)), [])),
    label: '', tipo: 'text', requerido: true,
  });
  const makeStep = schema => {
    const code = newCode('seccion', (schema.pasos || []).map(stepCode));
    return { id: code, key: code, bloque: code, title: '', description: '', scope: 'application', campos: [makeQuestion(schema)] };
  };
  if (!value) {
    return (
      <section className="announcement-form__card">
        <div className="announcement-form__section-header">
          <span className="announcement-form__section-icon"><HelpOutline /></span>
          <div>
            <span className="announcement-wizard__eyebrow">Formulario de postulación</span>
            <h3>Preguntas del paso 1</h3>
            <p>Organiza la información que necesitas para recibir las postulaciones al premio.</p>
          </div>
        </div>
        <div className="announcement-question__empty">
          <HelpOutline />
          <strong>Formulario predeterminado</strong>
          <span>Puedes conservarlo o personalizar sus secciones y preguntas.</span>
        </div>
        <button type="button" className="announcement-question__add-button" disabled={disabled} onClick={() => {
          const schema = { nombre: 'Formulario de postulación', pasos: [] };
          onChange({ ...schema, pasos: [makeStep(schema)] });
        }}><Add />Crear formulario personalizado</button>
      </section>
    );
  }
  const steps = Array.isArray(value.pasos) ? value.pasos : [];
  const applicationIndexes = steps.reduce((indexes, step, index) => isObject(step) && isApplication(step) ? [...indexes, index] : indexes, []);
  const updateStep = (index, next) => {
    identity(next, steps[index]);
    onChange({ ...value, pasos: steps.map((step, current) => current === index ? next : step) });
  };
  const updateField = (stepIndex, fieldIndex, next) => {
    const step = steps[stepIndex];
    identity(next, fieldsOf(step)[fieldIndex]);
    updateStep(stepIndex, withValue(step, 'campos', 'fields', fieldsOf(step).map((field, current) => current === fieldIndex ? next : field)));
  };
  const moveStep = (applicationIndex, direction) => {
    const next = steps.slice();
    const origin = applicationIndexes[applicationIndex];
    const destination = applicationIndexes[applicationIndex + direction];
    if (destination === undefined) return;
    [next[origin], next[destination]] = [next[destination], next[origin]];
    onChange({ ...value, pasos: next });
  };
  return (
    <div className="premio-editor__schema">
      <section className="announcement-form__card">
        <div className="announcement-form__section-header">
          <span className="announcement-form__section-icon"><HelpOutline /></span>
          <div>
            <span className="announcement-wizard__eyebrow">Formulario de postulación</span>
            <h3>Preguntas del paso 1</h3>
            <p>Agrupa las preguntas por tema y abre cada una para editar su texto, tipo de respuesta y alternativas.</p>
          </div>
        </div>
        <Control label="Nombre del formulario">
          <input type="text" value={textValue(value.nombre)} required maxLength={255} disabled={disabled} onChange={event => onChange({ ...value, nombre: event.target.value })} />
        </Control>
      </section>
      {applicationIndexes.map((stepIndex, applicationIndex) => {
        const step = steps[stepIndex];
        const fields = fieldsOf(step);
        return (
          <section className="announcement-form__card" key={identity(step)} aria-label={`Sección ${applicationIndex + 1}`}>
            <div className="announcement-form__section-header">
              <span className="announcement-form__section-icon"><ViewListOutlined /></span>
              <div>
                <span className="announcement-wizard__eyebrow">Sección {applicationIndex + 1}</span>
                <h3>{textValue(firstDefined(step.title, step.titulo)).trim() || 'Nueva sección'}</h3>
                <p>{fields.length} {fields.length === 1 ? 'pregunta' : 'preguntas'} del formulario de postulación.</p>
              </div>
              <OrderButtons label="sección" index={applicationIndex} length={applicationIndexes.length} onMove={direction => moveStep(applicationIndex, direction)} onRemove={() => onChange({ ...value, pasos: steps.filter((item, index) => index !== stepIndex) })} disabled={disabled} />
            </div>
            <Control label="Título de la sección">
              <input type="text" value={textValue(firstDefined(step.title, step.titulo))} required maxLength={255} disabled={disabled} onChange={event => updateStep(stepIndex, withValue(step, 'title', 'titulo', event.target.value))} />
            </Control>
            <Control label="Descripción de la sección">
              <textarea rows={2} maxLength={1000} value={textValue(firstDefined(step.description, step.descripcion))} disabled={disabled} onChange={event => updateStep(stepIndex, withValue(step, 'description', 'descripcion', event.target.value))} />
            </Control>
            <div className="announcement-question__list">
              {fields.map((field, fieldIndex) => <QuestionEditor
                key={identity(field)}
                editorId={identity(field)}
                field={field}
                index={fieldIndex}
                length={fields.length}
                disabled={disabled}
                fileUploadLimits={fileUploadLimits}
                onValidationError={onValidationError}
                onChange={next => updateField(stepIndex, fieldIndex, next)}
                onMove={direction => updateStep(stepIndex, withValue(step, 'campos', 'fields', moveItem(fields, fieldIndex, direction)))}
                onRemove={() => updateStep(stepIndex, withValue(step, 'campos', 'fields', fields.filter((item, index) => index !== fieldIndex)))}
              />)}
            </div>
            <button type="button" className="announcement-question__add-button announcement-question__add-button--bottom" disabled={disabled} onClick={() => updateStep(stepIndex, withValue(step, 'campos', 'fields', [...fields, makeQuestion(value)]))}><Add />Agregar pregunta</button>
            <details className="premio-editor__advanced">
              <summary>Configuración adicional de la sección</summary>
              <Control label="Código de la sección">
                <input type="text" value={stepCode(step)} required maxLength={64} disabled={disabled} onChange={event => updateStep(stepIndex, withValue(step, 'bloque', 'block', event.target.value))} />
              </Control>
              <p className="premio-editor__hint">Conserva el código para mantener la relación con las respuestas existentes.</p>
              <JsonEditor label="Opciones avanzadas de la sección" value={step} onChange={next => updateStep(stepIndex, next)} disabled={disabled} validate={next => validateStep(next, fileUploadLimits)} />
            </details>
          </section>
        );
      })}
      <button type="button" className="announcement-question__add-button" disabled={disabled} onClick={() => onChange({ ...value, pasos: [...steps, makeStep(value)] })}><Add />Agregar sección</button>
    </div>
  );
}
